import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import axios from 'axios';
import QRCode from 'react-qr-code';
import {
  FolderOpen, CreditCard, Download, LogOut, Home, FileText,
  Upload, Menu, X, ChevronRight, Lock, Unlock, Check, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/fe0mwn3b_image.png";

function ProjectPhaseView({ project }) {
  const phases = project.phases || [];

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await axios.get(`${API}/files/${fileId}/download`, { withCredentials: true, responseType: 'blob' });
      const contentType = res.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([res.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const [fileDetails, setFileDetails] = useState({});

  useEffect(() => {
    // Load file details for all deliverables that have file_ids
    const loadFiles = async () => {
      const allFileIds = [];
      phases.forEach(phase => {
        phase.deliverables?.forEach(del => {
          if (del.file_ids) allFileIds.push(...del.file_ids);
        });
      });
      if (allFileIds.length === 0) return;
      // Load each file's details
      const details = {};
      for (const fileId of allFileIds) {
        try {
          const files = await axios.get(`${API}/projects/${project.project_id}/files`, { withCredentials: true });
          files.data.forEach(f => { details[f.file_id] = f; });
          break; // Only need one call to get all project files
        } catch {}
      }
      setFileDetails(details);
    };
    loadFiles();
  }, [project, phases]);

  const statusIcon = (status) => {
    if (status === 'active') return <Unlock size={12} className="text-green-600" />;
    if (status === 'locked') return <Lock size={12} className="text-gray-400" />;
    if (status === 'completed') return <Check size={12} className="text-blue-600" />;
    return <Clock size={12} className="text-gray-400" />;
  };

  const progressPercent = () => {
    if (phases.length === 0) return 0;
    const completed = phases.filter(p => p.status === 'completed' || p.payment_status === 'verified').length;
    return Math.round((completed / phases.length) * 100);
  };

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-wider">Project Phases</p>
        <span className="text-[10px] text-[#5C5C5C]">{progressPercent()}% complete</span>
      </div>
      <Progress value={progressPercent()} className="h-1.5 rounded-none mb-4" />

      <div className="space-y-1">
        {phases.map((phase) => {
          const isAccessible = phase.status !== 'locked';
          return (
            <div key={phase.phase_number} className={`border border-[#E5E5E5] ${isAccessible ? 'bg-white' : 'bg-[#F5F5F5]'}`}>
              <div className="p-3">
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 flex items-center justify-center border text-xs font-bold ${
                    isAccessible ? 'border-[#121212] text-[#121212]' : 'border-[#E5E5E5] text-[#5C5C5C]'
                  }`}>
                    {phase.phase_number}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {statusIcon(phase.status)}
                      <p className={`text-sm font-medium ${!isAccessible ? 'text-[#5C5C5C]' : ''}`}>{phase.name}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] px-1.5 py-0.5 uppercase font-bold ${
                        phase.status === 'active' ? 'bg-green-100 text-green-800' :
                        phase.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>{phase.status}</span>
                      {phase.payment_amount > 0 && (
                        <span className={`text-[10px] px-1.5 py-0.5 uppercase font-bold ${
                          phase.payment_status === 'paid' || phase.payment_status === 'verified'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {phase.payment_amount?.toLocaleString()} - {phase.payment_status}
                        </span>
                      )}
                      {phase.payment_status === 'pending' && phase.payment_amount > 0 && isAccessible && (
                        <a href={`/payment?project=${project.project_id}&phase=${phase.phase_number}`}>
                          <span className="text-[10px] px-2 py-0.5 bg-green-600 text-white uppercase font-bold cursor-pointer hover:bg-green-700 transition-colors">
                            Pay Now
                          </span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {isAccessible && (
                  <div className="mt-3 ml-10 space-y-2">
                    {phase.deliverables?.map((del, dIdx) => (
                      <div key={dIdx} className="py-1.5 border-b border-[#E5E5E5] last:border-0">
                        <div className="flex items-center gap-2">
                          {del.status === 'released' ? (
                            <Check size={12} className="text-green-600" />
                          ) : (
                            <div className="w-3 h-3 border border-[#E5E5E5] rounded-sm" />
                          )}
                          <span className="text-xs">{del.name}</span>
                          <Badge variant={del.status === 'released' ? 'default' : 'outline'}
                            className="rounded-none text-[9px] uppercase">{del.status}</Badge>
                          {/* YouTube links with logo */}
                          {(del.youtube_links || (del.youtube_url ? [{ name: del.youtube_name, url: del.youtube_url }] : [])).map((yt, ytIdx) => yt.url && (
                            <a key={ytIdx} href={yt.url} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[10px] text-red-600 border border-red-300 px-1.5 py-0.5 hover:bg-red-600 hover:text-white transition-colors">
                              <svg width="12" height="9" viewBox="0 0 24 18">
                                <path d="M23.5 2.8c-.3-1-1-1.8-2-2C19.6.3 12 .3 12 .3S4.4.3 2.5.8c-1 .3-1.8 1-2 2C0 4.7 0 9 0 9s0 4.3.5 6.2c.3 1 1 1.8 2 2C4.4 17.7 12 17.7 12 17.7s7.6 0 9.5-.5c1-.3 1.8-1 2-2 .5-1.9.5-6.2.5-6.2s0-4.3-.5-6.2z" fill="#FF0000"/>
                                <path d="M9.6 12.8L15.8 9 9.6 5.2v7.6z" fill="#FFF"/>
                              </svg>
                              {yt.name || 'Watch Animation'}
                            </a>
                          ))}
                        </div>
                        {/* Files as bullet list with date */}
                        {del.status === 'released' && del.file_ids?.length > 0 && (
                          <ul className="ml-5 mt-1.5 space-y-1.5">
                            {del.file_ids.map(fId => {
                              const file = fileDetails[fId];
                              return file ? (
                                <li key={fId} className="flex items-center gap-1.5 flex-wrap">
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#121212] flex-shrink-0" />
                                  <FileText size={10} className="text-[#5C5C5C] flex-shrink-0" />
                                  <span className="text-[11px] truncate max-w-[180px]">{file.original_filename}</span>
                                  {file.size && <span className="text-[9px] text-[#999] flex-shrink-0">({(file.size / (1024*1024)).toFixed(1)} MB)</span>}
                                  {file.created_at && <span className="text-[9px] text-[#999] flex-shrink-0">{new Date(file.created_at).toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'})}</span>}
                                  {/* Preview - opens in correct format */}
                                  <button onClick={async () => {
                                    try {
                                      const res = await axios.get(`${API}/files/${fId}/download`, { withCredentials: true, responseType: 'blob' });
                                      const contentType = res.headers['content-type'] || file.content_type || 'application/octet-stream';
                                      const blob = new Blob([res.data], { type: contentType });
                                      const url = URL.createObjectURL(blob);
                                      window.open(url, '_blank');
                                    } catch {}
                                  }} className="text-[10px] text-green-700 border border-green-300 px-1.5 py-0.5 hover:bg-green-600 hover:text-white transition-colors flex-shrink-0 ml-auto">
                                    Preview
                                  </button>
                                  {/* Download - saves with correct filename */}
                                  <button onClick={() => downloadFile(fId, file.original_filename)}
                                    className="text-[10px] text-[#121212] border border-[#121212] px-1.5 py-0.5 hover:bg-[#121212] hover:text-white transition-colors flex-shrink-0">
                                    <Download size={9} className="inline mr-0.5" /> Download
                                  </button>
                                </li>
                              ) : null;
                            })}
                          </ul>
                        )}
                      </div>
                    ))}
                    {phase.notes && <p className="text-[10px] text-[#5C5C5C] italic mt-2">{phase.notes}</p>}
                  </div>
                )}

                {!isAccessible && (
                  <p className="text-[10px] text-[#5C5C5C] ml-10 mt-2 italic flex items-center gap-1">
                    <Lock size={9} /> This phase is locked. Complete previous phase payment to unlock.
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {phases.length === 0 && <p className="text-xs text-[#5C5C5C]">No phases defined for this project</p>}
    </div>
  );
}

export default function ClientDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState('projects');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [payments, setPayments] = useState([]);
  const [qrSettings, setQrSettings] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [qrBlobUrl, setQrBlobUrl] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [projRes, payRes, qrRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/payments`),
        axios.get(`${API}/admin/qr-settings`).catch(() => ({ data: null }))
      ]);
      setProjects(projRes.data);
      setPayments(payRes.data);
      setQrSettings(qrRes.data);
      // Load QR image as blob
      try {
        const qrImg = await axios.get(`${API}/admin/qr-image`, { responseType: 'blob' });
        setQrBlobUrl(URL.createObjectURL(new Blob([qrImg.data], { type: 'image/png' })));
      } catch {}
    } catch {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const uploadProof = async (paymentId, file) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/payments/${paymentId}/proof`, fd, { withCredentials: true });
      loadData();
    } catch {}
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  return (
    <div className="flex min-h-screen bg-[#FDFCFB]">
      <aside className={`${mobileOpen ? 'fixed inset-0 z-50' : 'hidden'} lg:block lg:relative lg:w-64 lg:border-r lg:border-[#E5E5E5] bg-white h-screen lg:sticky lg:top-0`}>
        {mobileOpen && <div className="absolute inset-0 bg-black/30 lg:hidden" onClick={() => setMobileOpen(false)} />}
        <div className="relative w-64 bg-white h-full flex flex-col">
          <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="S.A.I." className="h-8" />
              <div>
                <span className="text-sm font-bold">S.A.I.</span>
                <span className="text-[10px] tracking-[0.2em] uppercase block text-[#5C5C5C] -mt-0.5">Client Portal</span>
              </div>
            </a>
            <button className="lg:hidden" onClick={() => setMobileOpen(false)}><X size={18} /></button>
          </div>
          <div className="flex items-center gap-3 px-6 py-4 border-b border-[#E5E5E5]">
            {user?.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" /> :
              <div className="w-8 h-8 bg-[#E5E5E5] flex items-center justify-center text-xs font-bold">{user?.name?.charAt(0) || '?'}</div>}
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[10px] text-[#5C5C5C] truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex-1 py-4">
            {[{ id: 'projects', icon: FolderOpen, label: 'My Projects' }, { id: 'payments', icon: CreditCard, label: 'Payments' }].map(item => (
              <button key={item.id} data-testid={`client-nav-${item.id}`}
                onClick={() => { setActive(item.id); setMobileOpen(false); }}
                className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                  active === item.id ? 'bg-[#121212] text-white' : 'text-[#5C5C5C] hover:bg-[#F5F5F5] hover:text-[#121212]'
                }`}>
                <item.icon size={18} strokeWidth={1.25} /><span className="tracking-wide">{item.label}</span>
              </button>
            ))}
          </div>
          <div className="p-4 border-t border-[#E5E5E5]">
            <a href="/" className="flex items-center gap-3 px-6 py-2 text-sm text-[#5C5C5C] hover:text-[#121212]">
              <Home size={18} strokeWidth={1.25} /><span>Back to Home</span>
            </a>
            <button onClick={handleLogout} data-testid="client-logout-btn"
              className="w-full flex items-center gap-3 px-6 py-2 text-sm text-[#5C5C5C] hover:text-[#121212]">
              <LogOut size={18} strokeWidth={1.25} /><span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-[#E5E5E5] px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileOpen(true)} data-testid="client-mobile-menu"><Menu size={24} /></button>
          <span className="text-sm font-bold">Client Portal</span><div />
        </div>

        <div className="p-6 lg:p-10 max-w-5xl">
          {active === 'projects' && (
            <div data-testid="client-projects">
              <h2 className="text-xl font-bold mb-6">My Projects</h2>
              {projects.length === 0 ? (
                <div className="border border-[#E5E5E5] bg-white p-12 text-center">
                  <FolderOpen size={40} className="mx-auto text-[#E5E5E5] mb-4" strokeWidth={1} />
                  <p className="text-sm text-[#5C5C5C]">No projects assigned to you yet.</p>
                  <p className="text-xs text-[#5C5C5C] mt-1">Your architect will share designs here once they're ready.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {projects.map(proj => (
                    <div key={proj.project_id} className="border border-[#E5E5E5] bg-white">
                      <div className="p-4 cursor-pointer hover:bg-[#F5F5F5] transition-colors"
                        onClick={() => setSelectedProject(selectedProject === proj.project_id ? null : proj.project_id)}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-bold">{proj.title}</p>
                              <span className="text-[10px] text-[#5C5C5C] font-mono">{proj.project_id}</span>
                            </div>
                            <p className="text-xs text-[#5C5C5C] mt-1">{proj.description}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="rounded-none text-[10px] uppercase">{proj.status}</Badge>
                            <ChevronRight size={16} className={`transition-transform ${selectedProject === proj.project_id ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                      </div>
                      {selectedProject === proj.project_id && (
                        <div className="border-t border-[#E5E5E5] p-4">
                          <ProjectPhaseView project={proj} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {active === 'payments' && (
            <div data-testid="client-payments">
              <h2 className="text-xl font-bold mb-6">Payments</h2>
              {/* Payment QR - show uploaded image for users with projects, dummy for users without */}
              <div className="border border-[#E5E5E5] bg-white p-6 mb-6">
                <h3 className="text-sm font-bold uppercase tracking-wider mb-4">Payment QR Code</h3>
                <div className="flex flex-col items-center">
                  {projects.length > 0 && qrSettings?.upi_id ? (
                    <>
                      <div className="border border-[#E5E5E5] p-3 bg-white">
                        {qrBlobUrl ? (
                          <img src={qrBlobUrl} alt="Payment QR" className="w-56 h-auto" />
                        ) : (
                          <QRCode value={`upi://pay?pa=${qrSettings.upi_id}&pn=${encodeURIComponent(qrSettings.merchant_name || 'S.A.I. Design Group')}&cu=INR`}
                            size={200} level="M" />
                        )}
                      </div>
                      <p className="text-xs text-[#5C5C5C] mt-3">Scan with any UPI app - Paytm / Google Pay / PhonePe / BHIM</p>
                      <p className="text-xs font-medium mt-1">{qrSettings.upi_id}</p>
                    </>
                  ) : (
                    <>
                      <div className="border border-[#E5E5E5] p-4 bg-[#F5F5F5]">
                        <QRCode value="https://saidesigngroup.com" size={150} level="L" fgColor="#CCCCCC" />
                      </div>
                      <p className="text-xs text-[#5C5C5C] mt-3">No active projects assigned yet.</p>
                      <p className="text-[10px] text-[#5C5C5C] mt-1">Payment QR will appear once a project is assigned to you.</p>
                    </>
                  )}
                </div>
              </div>
              <div className="border border-[#E5E5E5] bg-white divide-y divide-[#E5E5E5]">
                {payments.map(pay => (
                  <div key={pay.payment_id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{pay.currency || 'INR'} {pay.amount?.toLocaleString()}</p>
                        <p className="text-xs text-[#5C5C5C]">{pay.description}</p>
                        <p className="text-[10px] text-[#5C5C5C] mt-1">{new Date(pay.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={pay.status === 'completed' ? 'default' : 'outline'}
                          className="rounded-none text-[10px] uppercase">{pay.status}</Badge>
                        {(pay.status === 'pending') && (
                          <label className="cursor-pointer">
                            <input type="file" accept="image/*" className="hidden"
                              data-testid={`upload-proof-${pay.payment_id}`}
                              onChange={(e) => e.target.files[0] && uploadProof(pay.payment_id, e.target.files[0])} />
                            <span className="inline-flex items-center gap-1 text-xs border border-[#121212] px-3 py-1.5 hover:bg-[#121212] hover:text-white transition-colors cursor-pointer">
                              <Upload size={10} /> Upload Proof
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {payments.length === 0 && <p className="p-6 text-sm text-[#5C5C5C] text-center">No payments due</p>}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
